import { FC, useCallback, useMemo, useState } from 'react';
import { IProcurementFilesCategory } from 'src/models/procurements/Tender/types';
import React from 'react';
import styles from './index.module.scss';
import Sidebar from './Sidebar';
import Content from './Content';
import {
  useFilteredDocuments,
  useHandleDocuments,
  useSearchProfiles,
  useSearchResultsToDocuments,
  useSearchTerms,
  useDocumentOccurrence,
  useAllTermOccurrence
} from './hooks';
import { Button, Modal, Skeleton } from 'src/common';
import EmptyScreen from './EmptyScreen';
import { ISearchSettings, TermsForSearch } from './types';
import { AggregateBy } from './Sidebar/AggregateSelect/types';
import { trackDocumentSearchDoSearch } from 'src/segment/events';
import SearchProfiles from './SearchProfiles';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import { FilteredProfileTermsContext, ProfileTermsContext } from './context';
import { extractFiles } from './helpers';

interface IDocumentSearchProps {
  procurementId: string;
  procurementFiles: IProcurementFilesCategory;
}

const AGGREGATE_BY_INITIAL = AggregateBy.SearchProfile;
export const DocumentSearch: FC<IDocumentSearchProps> = props => {
  const { t } = useTranslation();
  const { procurementId, procurementFiles } = props;
  const { data: searchProfilesData, loading: searchProfileLoading, error: searchProfileError } = useSearchProfiles();
  const searchProfiles = useMemo(() => searchProfilesData || [], [searchProfilesData]);
  const defaultSetting: ISearchSettings = {
    aggregateBy: AGGREGATE_BY_INITIAL,
    groups: [],
    documents: []
  };
  const [settings, setSettings] = useState<ISearchSettings>(defaultSetting);

  const termsForSearch = useMemo((): TermsForSearch => {
    return searchProfiles.map(searchProfile => ({
      searchProfile,
      terms: searchProfile.terms
    }));
  }, [searchProfiles]);
  const {
    data: searchResults,
    error: searchError,
    loading: searchLoading
  } = useSearchTerms(procurementId, termsForSearch, searchProfileLoading);

  const handleSearchSettingsChange = useCallback(
    (settings: ISearchSettings): void => {
      setSettings(settings);
      trackDocumentSearchDoSearch(
        {
          id: procurementId
        },
        settings
      );
    },
    [procurementId]
  );

  const sidebarGroups = useMemo(() => searchProfilesData || [], [searchProfilesData]);
  const files = useMemo(() => extractFiles(procurementFiles), [procurementFiles]);
  const documents = useSearchResultsToDocuments(searchResults || {}, searchProfiles);
  const handledDocuments = useHandleDocuments(documents, searchProfiles);
  const filteredDocuments = useFilteredDocuments(handledDocuments, settings);
  const { sum: totalTermsCount } = useDocumentOccurrence(handledDocuments, defaultSetting);
  const { map: occMap, sum: occSum } = useAllTermOccurrence(filteredDocuments);
  const filteredTermsCount = useMemo(() => {
    let sum = 0;
    let count = 0;
    settings.groups.forEach(group => {
      group.terms.forEach(term => {
        count++;
        sum += occMap.get(term) || 0;
      });
    });
    return count > 0 ? sum : occSum;
  }, [occMap, occSum, settings.groups]);
  const [searchProfilesVisible, toggleSearchProfilesVisible] = useToggle(false);

  return (
    <>
      {searchProfileLoading && <Skeleton loading active />}
      {!searchProfileLoading && (
        <>
          {!!searchProfiles.length ? (
            <FilteredProfileTermsContext.Provider value={settings.groups}>
              <div className={styles.container}>
                <Content
                  error={searchError}
                  loading={searchLoading}
                  documents={filteredDocuments}
                  searchProfiles={sidebarGroups}
                  aggregateBy={settings.aggregateBy}
                  totalTermsCount={totalTermsCount}
                  filteredTermsCount={filteredTermsCount}
                />
                <Sidebar
                  aggregateByInitialValue={AGGREGATE_BY_INITIAL}
                  loading={searchProfileLoading}
                  error={searchProfileError}
                  searchProfiles={sidebarGroups}
                  files={files}
                  documents={handledDocuments}
                  settings={settings}
                  onSettingsChange={handleSearchSettingsChange}
                  toggleSearchProfilesVisible={toggleSearchProfilesVisible}
                />
              </div>
            </FilteredProfileTermsContext.Provider>
          ) : (
            <EmptyScreen toggleSearchProfilesVisible={toggleSearchProfilesVisible} />
          )}
          <Modal
            width={838}
            title={t('KeywordSearch.documentsSearch')}
            visible={searchProfilesVisible}
            onCancel={toggleSearchProfilesVisible}
            footer={
              <Button type={'primary'} onClick={toggleSearchProfilesVisible} data-action={'close'}>
                {t('Common.done')}
              </Button>
            }
          >
            <ProfileTermsContext.Provider value={termsForSearch}>
              <SearchProfiles />
            </ProfileTermsContext.Provider>
          </Modal>
        </>
      )}
    </>
  );
};

export default DocumentSearch;
